import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "./api/client";
import { meQuery } from "./api/platform";
import type { AuthContextValue } from "./auth/auth-context";
import { safeReturnTo } from "./auth/session";
import { CenteredPage } from "./components/centered-page";
import { ErrorMessage } from "./components/error-message";
import { CallbackPage } from "./routes/callback-page";
import { InvitePage } from "./routes/invite-page";
import { MembersPage } from "./routes/members-page";
import { OnboardingPage } from "./routes/onboarding-page";
import { OverviewPage } from "./routes/overview-page";
import { SelectPage } from "./routes/select-page";
import { StartPage } from "./routes/start-page";
import { TenantLayout } from "./routes/tenant-layout";

// =============================================================================
// Routen (TanStack Router, Entscheidung 36) – im Code definiert
// =============================================================================
//   /                      Startseite mit Anmeldung (Entscheidung 37)
//   /callback              Rücksprung von Cognito
//   /select                Mandantenwahl nach dem Login            ┐
//   /onboarding            eigenen Mandanten anlegen (Trial)        │ nur
//   /invite#<token>        Einladung annehmen                       │ angemeldet
//   /t/$tenant             Mandantenbereich (Entscheidung 35)       │ (_authed)
//   /t/$tenant/members     Mitglieder + Einladungslink              ┘

// Was jede Route über `context` erreicht. Typisiert, damit beforeLoad und
// loader wissen, was es gibt.
export interface RouterContext {
  auth: AuthContextValue;
  api: ApiClient;
  queryClient: QueryClient;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <CenteredPage>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink">{t("errors.not_found")}</p>
        <Link to="/" className="text-sm text-link-fg hover:underline">
          {t("app.backToStart")}
        </Link>
      </div>
    </CenteredPage>
  );
}

function RouteError({ error }: { error: unknown }) {
  return (
    <CenteredPage>
      <ErrorMessage error={error} />
    </CenteredPage>
  );
}

function Loading() {
  const { t } = useTranslation();
  return <p className="p-6 text-sm text-ink-muted">{t("app.loading")}</p>;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
  pendingComponent: Loading,
});

// --- öffentlich --------------------------------------------------------------

const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  // Suchparameter prüfen: nur ein sicherer, relativer Rücksprungpfad zählt.
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => {
    const returnTo = safeReturnTo(search["returnTo"]);
    return returnTo ? { returnTo } : {};
  },
  beforeLoad: async ({ context, search }) => {
    const target = search.returnTo ?? "/select";
    if (context.auth.getState().status === "authenticated") {
      throw redirect({ href: target });
    }
    // Variante A (Entscheidung 39): nach Neuladen direkt zum zuletzt genutzten
    // Anbieter. Cognito leitet dabei die Seite um (login kehrt nicht zurück).
    const provider = context.auth.session.lastProvider();
    if (provider) await context.auth.login({ provider, returnTo: target });
  },
  component: StartPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/callback",
  component: CallbackPage,
});

// --- nur angemeldet ------------------------------------------------------------
// Pfadlose Layout-Route ("_authed"): prüft die Anmeldung einmal für alle
// Unterseiten. Nicht angemeldet -> zum Anbieter bzw. zur Startseite, mit
// Rücksprung auf die angefragte Seite (inkl. #token bei Einladungen).
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  beforeLoad: async ({ context, location }) => {
    if (context.auth.getState().status === "authenticated") return;
    const returnTo =
      location.pathname + location.searchStr + (location.hash ? `#${location.hash}` : "");
    const provider = context.auth.session.lastProvider();
    if (provider) await context.auth.login({ provider, returnTo });
    throw redirect({ to: "/", search: { returnTo } });
  },
  component: Outlet,
});

// Lädt die Mitgliedschaften vorab, damit die Seiten sie sofort haben.
// ensureQueryData nutzt den Cache, wenn die Daten schon da sind.
const loadMe = ({ context }: { context: RouterContext }) =>
  context.queryClient.ensureQueryData(meQuery(context.api));

const selectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/select",
  loader: loadMe,
  component: SelectPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/onboarding",
  component: OnboardingPage,
});

const inviteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/invite",
  component: InvitePage,
});

const tenantRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/t/$tenant",
  loader: loadMe,
  component: TenantLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/",
  component: OverviewPage,
});

const membersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/members",
  component: MembersPage,
});

const routeTree = rootRoute.addChildren([
  startRoute,
  callbackRoute,
  authedRoute.addChildren([
    selectRoute,
    onboardingRoute,
    inviteRoute,
    tenantRoute.addChildren([overviewRoute, membersRoute]),
  ]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    // Loader-Daten verwaltet TanStack Query; der Router soll sie nicht
    // zusätzlich cachen (sonst zwei Caches mit unterschiedlichem Stand).
    defaultPreloadStaleTime: 0,
  });
}

// Registriert den Router-Typ global: Link, useNavigate usw. kennen damit alle
// Pfade und Parameter – ein falscher Pfad ist ein Compile-Fehler.
declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
